import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'react-feather';
import { TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import MicrocreditQuestionnaire from './MicrocreditQuestionnaire';
import Button from '@/UIlibrary/Button/Button';
import ProfileDrawer from './ProfileDrawer';
import {
	MicrocreditRequest,
	MicrocreditRequestStatus,
	OnboardingStatus,
	useGetMicrocreditRequestQuery
} from '@/graphql/__generated__/credit-requests';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import MicrocreditSimulation from './MicrocreditSimulation';
import RejectModal from './MicrocreditRequestDetails/RejectModal';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import Banner from '@/UIlibrary/Banner/Banner';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import CreditCover from './MicrocreditRequestDetails/CreditCover';
import CreditCommitteeRuling from './MicrocreditRequestDetails/CreditCommitteeRuling';

const MicrocreditRequestDetails: React.FC = () => {
	const t = useTranslations('MicrocreditRequestDetails');
	const [activeTab, setActiveTab] = useState(0);
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const [drawerWidth, setDrawerWidth] = useState(300);
	const [isResizing, setIsResizing] = useState(false);
	const { requestId, lang } = useParams();

	const microcreditsClient = useApolloClient(SCHEMAS.MICROCREDITS);
	const { loading, data, error, refetch } = useGetMicrocreditRequestQuery({
		client: microcreditsClient,
		variables: { id: requestId || '' },
		errorPolicy: 'all'
	});

	const [request, setRequest] = useState<MicrocreditRequest | null>(null);
	const [isRejectClientModalOpen, setIsRejectClientModalOpen] = useState(false);
	const clientCanBeRejectedStatus = [MicrocreditRequestStatus.COMPLETED];
	const canAccessCommitteeTab = [
		MicrocreditRequestStatus.PASSED_TO_CREDIT_COMMITTEE,
		MicrocreditRequestStatus.APPROVED_BY_CREDIT_COMMITTEE,
		MicrocreditRequestStatus.REJECTED_BY_CREDIT_COMMITTEE
	];

	const fullName = request
		? `${request.names || ''} ${request.firstLastName || ''} ${request.secondLastName || ''}`.trim()
		: '';

	const tabs = [
		{
			title: t('Questionnaire'),
			content: <MicrocreditQuestionnaire requestData={request} refetchData={refetch} />,
			badgeVisible: false
		},
		{
			title: t('MicrocreditSimulation'),
			content: <MicrocreditSimulation requestData={request} refetchData={refetch} />,
			disabled: !request?.callComments || request?.onboardingStatus === OnboardingStatus.REJECTED,
			disabledTooltip:
				request?.onboardingStatus === OnboardingStatus.REJECTED
					? t('OnboardingRejectedDisabled')
					: t('SimulationDisabled')
		},
		{
			title: t('CreditCover'),
			content: <CreditCover requestData={request} refetchData={refetch} />,
			badgeVisible: false,
			disabled:
				!request?.callComments ||
				request?.simulation === null ||
				request?.evaluationMatrix === null ||
				request?.onboardingStatus === OnboardingStatus.REJECTED,
			disabledTooltip:
				request?.onboardingStatus === OnboardingStatus.REJECTED
					? t('OnboardingRejectedDisabled')
					: t('CreditCoverDisabled')
		},
		{
			title: t('CreditCommittee'),
			content: <CreditCommitteeRuling requestData={request} refetchData={refetch} />,
			badgeVisible: false,
			disabled:
				!canAccessCommitteeTab.includes(
					request?.microcreditRequestStatus as MicrocreditRequestStatus
				) || request?.onboardingStatus === OnboardingStatus.REJECTED,
			disabledTooltip:
				request?.onboardingStatus === OnboardingStatus.REJECTED
					? t('OnboardingRejectedDisabled')
					: t('CreditCommitteeDisabled')
		}
	];

	const handleTabChange = (index: number) => {
		setActiveTab(index);
	};

	const handleClearQuestionnaireChanges = () => {
		const { resetForm } = useUpdateQuestionnaireStore.getState();
		resetForm();
	};

	useEffect(() => {
		if (data?.node) {
			setRequest(data.node as MicrocreditRequest);
		}
	}, [data]);

	useEffect(() => {
		document.body.style.userSelect = isResizing ? 'none' : '';
		return () => {
			document.body.style.userSelect = '';
		};
	}, [isResizing]);

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col justify-center items-center h-screen gap-4 p-4">
				<Typography size="md" color="error">
					{error.message}
				</Typography>
			</div>
		);
	}

	return (
		<div className="flex relative">
			<div
				id="main-content"
				className="flex-1 flex flex-col min-h-0"
				style={{
					marginRight: isProfileOpen ? `${drawerWidth}px` : 0,
					transition: isResizing ? 'none' : 'margin-right 300ms'
				}}
			>
				<div className="sticky top-0 bg-white z-30 print:hidden">
					<div className="flex flex-row items-center justify-between gap-4 min-h-16 p-4">
						<div className="flex items-center gap-2 w-3/4">
							<Link to={`..`} onClick={handleClearQuestionnaireChanges}>
								<ArrowLeft size={24} color={BRAND_COLORS.brand600} />
							</Link>
							<div className="flex flex-col gap-3 justify-left">
								<div className="flex flex-col gap-1">
									<Typography size="sm" type="display">
										{fullName}
									</Typography>
									<div>
										<Button
											label={t('ViewProfile')}
											onClick={() => setIsProfileOpen(true)}
											size="small"
											textOnly
										/>
									</div>
								</div>
								<Typography size="sm" color="subtitle">
									{t('RequestID')}: {request?.microcreditRequestId || ''}
								</Typography>
								{request?.microcreditRequestStatus === MicrocreditRequestStatus.REJECTED && (
									<Banner
										message={t('RejectReasonLabel')}
										secondaryMessage={request?.rejectionReason || ''}
										variant="error"
									/>
								)}
							</div>
						</div>
						<div className="flex items-center gap-4">
							<Tooltip
								content={
									clientCanBeRejectedStatus.includes(
										request?.microcreditRequestStatus as MicrocreditRequestStatus
									)
										? ''
										: t('RejectDisabledTooltip')
								}
								id="reject-tooltip"
							>
								<Button
									label={t('RejectMicrocredit')}
									onClick={() => setIsRejectClientModalOpen(true)}
									size="auto"
									color="error"
									disabled={
										!clientCanBeRejectedStatus.includes(
											request?.microcreditRequestStatus as MicrocreditRequestStatus
										)
									}
								/>
							</Tooltip>
						</div>
					</div>
					<hr />
				</div>

				<div className="flex-1 overflow-y-auto min-h-0 max-h-[calc(78vh)]">
					<div className="w-full p-4">
						<TabMenu
							activeTabIndex={activeTab}
							tabs={tabs}
							maxWidth={1500}
							onTabChanged={handleTabChange}
						/>
					</div>
				</div>
			</div>

			<ProfileDrawer
				isOpen={isProfileOpen}
				onClose={() => setIsProfileOpen(false)}
				amountRequested={request?.creditDetails?.amountRequested || ''}
				clientId={request?.clientId || ''}
				fullName={fullName}
				email={request?.email || ''}
				phoneNumber={request?.phoneNumber || ''}
				deliberation={request?.onboardingRegistryDeliberation || ''}
				t={t}
				lang={lang || 'es'}
				drawerWidth={drawerWidth}
				onDrawerWidthChange={setDrawerWidth}
				isResizing={isResizing}
				onResizingChange={setIsResizing}
			/>
			<RejectModal
				isOpen={isRejectClientModalOpen}
				onClose={() => setIsRejectClientModalOpen(false)}
				onSuccess={async () => {
					await refetch();
				}}
				microcreditRequestId={request?.microcreditRequestId || ''}
			/>
		</div>
	);
};

export default MicrocreditRequestDetails;
